// Copyright (C) 2026 FlexiProof. All rights reserved.
//
// This source code file is a part of free software licensed under the terms of
// the MIT License as published by the Massachusetts Institute of Technology:
// you can use, copy, modify and distribute any part of it without limitation,
// subject to the conditions contained within that license.
//
// This source code file, and the software it forms a part of, IS PROVIDED "AS
// IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED. See the MIT License
// for more details.
//
// You should have received a copy of the MIT License along with this source
// code file in the root of this repository. If not, see one of the following
// 3rd party sites for a copy of the license template:
// - <https://opensource.org/licenses/MIT>
// - <https://choosealicense.com/licenses/mit>
// - <https://spdx.org/licenses/MIT>

/**
 * @file <INSERT_FILE_DESCRIPTION_HERE>
 * @author James Reid
 */

// @ts-check

// @@imports-package
import { epdmConfig } from "../config.js"
import {
    Product,
    Adhesive,
    Corner,
    Drain,
    Extra,
    Membrane,
    Tape,
    Trim
} from "../products/index.js"

// @@imports-module
import { Bom } from "./Bom.js"
import { regexps } from "./regexps.js"

// @@imports-types
import {
    Config,
    // LineItem,
    ProductData,
    AdhesiveType,
    EdgeType,
    TapeType,
    DrainType,
    ExtraType,
    EDGE_TYPES,
    ADHESIVE_TYPES,
    // RawLineItem,
    PatchType
} from "../types/index.js"
import { LineItem, RawLineItem } from "../types/LineItem.js"

// @@body
class Stock {
    #products = /** @type {Map.<string,Product>} */ (new Map())
    #calculatorConfig

    /**
     * 
     * @param {Config} calculatorConfig
     */
    constructor(calculatorConfig = epdmConfig) {
        this.#calculatorConfig = calculatorConfig
    }

    /**
     * @param {ProductData} data
     * @param {string} code
     */
    addProduct(data, code = data.skus.calculator) {
        // 
        let ProductClass, match
        if ((match = code.match(regexps.membranes))) { ProductClass = Membrane }
        else if ((match = code.match(regexps.adhesives))) {
            ProductClass = Adhesive
        }
        else if ((match = code.match(regexps.tapes))) { ProductClass = Tape }
        else if ((match = code.match(regexps.trims))) { ProductClass = Trim }
        else if ((match = code.match(regexps.corners))) {
            ProductClass = Corner
        }
        else if ((match = code.match(regexps.drains))) { ProductClass = Drain }
        else if (
            (match = code.match(regexps.extras)) ||
            (match = code.match(regexps.kits))
        ) { ProductClass = Extra }

        // 
        if (ProductClass && match) {
            const product = new ProductClass(data, match)
            this.#products
                .set(code.match(/(?<=^_[0-9A-F]{4}_).*/)?.[0] || "", product)
        }
    }

    /**
     * 
     * @param {string} code 
     * @returns {Product|undefined}
     */
    getProduct(code) {
        return this.#products.get(code)
    }

    getProductByUuid(uuid) {
        for (const [, product] of this.#products) {
            if (product.uuid === uuid) { return product }
        }
    }

    /**
     * 
     * @returns {Membrane[]}
     */
    getMembranes() {
        const products = /** @type {Membrane[]} */ ([])
        for (const [, product] of this.#products) {
            if (
                product instanceof Membrane &&
                product.type === this.#calculatorConfig.membrane.type &&
                product.thickness === this.#calculatorConfig.membrane.thickness
            ) { products.push(product) }
        }

        return products
    }

    /**
     * 
     * @param {AdhesiveType} type 
     * @returns {Adhesive[]}
     */
    getAdhesives(type) {
        const { membrane: { type: membraneType } } = this.#calculatorConfig

        /** @type {string[]} */
        const sealantTypes = Object.values(ADHESIVE_TYPES.sealants)
        const isSealant = sealantTypes.includes(type)

        const isMembraneIndependent = isSealant ||
            type === ADHESIVE_TYPES.deck.contactBonding ||
            type === ADHESIVE_TYPES.deck.pu || // note that this must *always* be fleece...
            type === ADHESIVE_TYPES.primer

        const products = /** @type {Adhesive[]} */ ([])
        for (const [, product] of this.#products) {
            if (
                product instanceof Adhesive &&
                product.type === type &&
                (isMembraneIndependent || product.membraneType === membraneType)
            ) { products.push(product) }
        }

        return products
    }

    /**
     * 
     * @param {TapeType} tapeType 
     * @param {object} obj
     * @param {PatchType} [obj.patchType]
     * @returns {Tape[]}
     */
    getTapes(tapeType, { patchType } = {}) {
        const products = /** @type {Tape[]} */ ([])
        for (const [, product] of this.#products) {
            if (product instanceof Tape && product.type === tapeType) {
                if (!patchType || product.patchType === patchType) {
                    products.push(product)
                }
            }
        }

        return products
    }

    /**
     * 
     * @param {EdgeType} edgeType 
     * @returns {Trim[]}
     */
    getTrims(edgeType) {
        const {
            trims: { type, color, depth, minLength, maxLength }
        } = this.#calculatorConfig

        const products = /** @type {Trim[]} */ ([])
        for (const [, product] of this.#products) {
            if (!(product instanceof Trim)) { continue }
            if (edgeType === EDGE_TYPES.parapetWall) {
                if (product.edgeType !== EDGE_TYPES.houseWall) { continue }
            }
            else if (product.edgeType !== edgeType) { continue }

            switch (edgeType) {
                case EDGE_TYPES.raisedKerb:
                case EDGE_TYPES.gutterDrip:
                    if (
                        product.type === type &&
                        product.color === color &&
                        product.quantity >= minLength &&
                        product.quantity <= maxLength
                    ) { products.push(product) }
                    break
                case EDGE_TYPES.houseWall:
                case EDGE_TYPES.parapetWall:
                    if (product.trimDepth === depth) { products.push(product) }
                    break
                case EDGE_TYPES.feltJoin:
                    products.push(product)
                    break
                default:
            }
        }

        return products
    }

    /**
     * 
     * @param {EdgeType} edgeType 
     * @param {number} cornerAngle
     * @returns {Corner[]}
     */
    getCorners(edgeType, cornerAngle) {
        const products = /** @type {Corner[]} */ ([])
        for (const [, product] of this.#products) {
            if (
                product instanceof Corner &&
                product.edgeType === edgeType &&
                product.cornerAngle === cornerAngle &&
                product.type === this.#calculatorConfig.trims.type &&
                product.color === this.#calculatorConfig.trims.color
            ) { products.push(product) }
        }

        return products
    }

    /**
     * 
     * @param {DrainType} type 
     * @returns {Drain[]}
     */
    getDrains(type) {
        const products = /** @type {Drain[]} */ ([])
        for (const [, product] of this.#products) {
            if (product instanceof Drain && product.type === type) {
                products.push(product)
            }
        }

        return products
    }

    /**
     * 
     * @param {ExtraType} type 
     * @returns {Extra[]}
     */
    getExtras(type) {
        const products = /** @type {Extra[]} */ ([])
        for (const [, product] of this.#products) {
            if (product instanceof Extra && product.type === type) {
                products.push(product)
            }
        }

        return products
    }

    /**
     * 
     * @param {Bom} bom 
     */
    updateRunningStockQuantities(bom) {
        for (const { sku, quantity } of bom.reducedLineItems) {
            const product = this.getProduct(sku)
            if (!product) { continue }
            product.runningStock -= quantity
        }
    }

    /**
     * 
     */
    resetRunningStockQuantities() {
        for (const [, product] of this.#products) {
            product.resetRunningStockQuantity()
        }
    }

    /**
     * add ignore for sku with 0 quantity... 
     * @param {Bom} bom 
     * @returns {Bom}
     */
    getQuantityRectifiedBom(bom) {
        const rectifiedBom = new Bom()

        for (const lineItem of bom.lineItems) {
            const { detail } = lineItem
            const {
                product,
                quantity
            } = this.#getQuantityRectifiedLineItem(lineItem)
            rectifiedBom.addLineItem(product, quantity, { detail })
        }

        return rectifiedBom
    }

    /**
     * Check if stock is sufficient to fulfill a given bill of materials.
     * @param {Bom} bom 
     * @returns {boolean} Boolean reflecting if there is sufficient stock to
     * fulfill the given bill of materials.
     */
    canFulfillBom(bom) {
        for (const { product, quantity } of bom.reducedLineItems) {
            if (quantity > product.stock - product.stockLimit) { return false }
        }

        return true
    }

    /**
     * 
     * @param {RawLineItem[]} rawLineItems 
     * @param {object} obj
     * @param {boolean} [obj.logMissing]
     * @returns {boolean} 
     */
    canFulfillRawLineItems(rawLineItems, { logMissing = false } = {}) {
        for (const { sku, quantity } of rawLineItems) {
            const product = this.getProduct(sku)

            if (!product) {
                if (logMissing) {
                    console.warn(`Product with sku ${sku} not found.`)
                }
                return false
            }

            const { name, stock } = product
            if (stock < quantity) {
                if (logMissing) {
                    console.warn(`${quantity} units of ${name} requested. Available stock of ${stock} reported.`)
                }
                return false
            }
            product.stock -= quantity
        }

        return true
    }

    /**
     * 
     * @param {LineItem} lineItem 
     * @returns {LineItem}
     */
    #getQuantityRectifiedLineItem(lineItem) {
        const { product, quantity } = lineItem

        const { minimumOrder, minimumFraction } = product
        const precision = minimumFraction >= 1 ? 0
            : minimumFraction <= 0 ? Infinity
            : Math.ceil(- Math.log10(minimumFraction))

        const ceilingQuantity = quantity + minimumFraction / 2 - 1e-8
        const rectifiedQuantity = isFinite(precision)
            ? parseFloat(ceilingQuantity.toFixed(precision))
            : quantity

        return {
            ...lineItem,
            quantity: Math.max(rectifiedQuantity, minimumOrder)
        }
    }

    get products() {
        return [...this.#products.values()]
    }

    set calculatorConfig(epdmConfig) {
        this.#calculatorConfig = epdmConfig
    }
}

// @@exports
export { Stock }
